/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Event schedule, break times, and calendar cell generation for CustomCalendar.
 * @module utils/calendar/calendarTimeUtils
 */
import { logger, sanitizeError } from "../logger.js";

/** @param {Object|null|undefined} eventData @returns {number} Duration in minutes (defaults to 60). */
export const getAppointmentDurationMinutes = (eventData) => {
  if (eventData && eventData.duracionCitas) {
    const [hours, minutes] = eventData.duracionCitas.split(":").map(Number);
    return hours * 60 + minutes;
  }
  return 60;
};

export const getCellsPerHour = (durationMinutes) =>
  Math.floor(60 / durationMinutes);

export const isTimeSlotWithinEventEnd = (
  hour,
  minute,
  durationMinutes,
  eventData,
) => {
  if (!eventData || !eventData.horaFin) {
    return true;
  }

  const [eventEndHour, eventEndMinute] = eventData.horaFin.split(":").map(Number);
  const eventEndMinutes = eventEndHour * 60 + eventEndMinute;
  const slotStartMinutes = hour * 60 + minute;
  const slotEndMinutes = slotStartMinutes + durationMinutes;

  return slotEndMinutes <= eventEndMinutes;
};

export const parseBreakTimes = (participatingCompanyData) => {
  if (!participatingCompanyData || !participatingCompanyData.tiempoBreak) {
    return null;
  }

  try {
    const [startTime, endTime] =
      participatingCompanyData.tiempoBreak.split("-");
    if (!startTime || !endTime) {
      return null;
    }

    return {
      horaInicioBreak: startTime.trim(),
      horaFinBreak: endTime.trim(),
    };
  } catch (error) {
    logger.error("Error parsing tiempoBreak:", { err: sanitizeError(error) });
    return null;
  }
};

export const getBreakMinutes = (breakTimes) => {
  if (!breakTimes?.horaInicioBreak || !breakTimes?.horaFinBreak) {
    return { breakStartMinutes: null, breakEndMinutes: null };
  }

  const [breakStartHour, breakStartMinute] = breakTimes.horaInicioBreak
    .split(":")
    .map(Number);
  const [breakEndHour, breakEndMinute] = breakTimes.horaFinBreak
    .split(":")
    .map(Number);

  return {
    breakStartMinutes: breakStartHour * 60 + breakStartMinute,
    breakEndMinutes: breakEndHour * 60 + breakEndMinute,
  };
};

export const isTimeSlotOverlappingBreak = (timeSlot, breakTimes) => {
  const { breakStartMinutes, breakEndMinutes } = getBreakMinutes(breakTimes);
  if (breakStartMinutes === null || breakEndMinutes === null) {
    return false;
  }

  const slotStartMinutes = timeSlot.hour * 60 + timeSlot.minute;
  const slotEndMinutes = slotStartMinutes + timeSlot.durationMinutes;

  return (
    (slotStartMinutes >= breakStartMinutes &&
      slotStartMinutes < breakEndMinutes) ||
    (slotEndMinutes > breakStartMinutes &&
      slotEndMinutes <= breakEndMinutes) ||
    (slotStartMinutes <= breakStartMinutes &&
      slotEndMinutes >= breakEndMinutes)
  );
};

export const generateCalendarHourLabels = (eventData, language) => {
  const labels = [];
  let startHour = 8;
  let endHour = 19;

  if (eventData?.horaInicio && eventData?.horaFin) {
    const startTimeParts = eventData.horaInicio.split(":");
    const endTimeParts = eventData.horaFin.split(":");
    startHour = parseInt(startTimeParts[0], 10);
    endHour = parseInt(endTimeParts[0], 10);

    if (parseInt(endTimeParts[1], 10) > 0) {
      endHour = parseInt(endTimeParts[0], 10);
    }
  }

  for (let hour = startHour; hour <= endHour; hour++) {
    const cellTime = new Date(2000, 0, 1, hour, 0);
    const timeString = cellTime
      .toLocaleTimeString(language, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/a\.\s*m\./gi, "AM")
      .replace(/p\.\s*m\./gi, "PM")
      .replace(
        /^(\d{1,2}):(\d{2})\s*(AM|PM)$/,
        (match, hourPart, minute, period) => {
          const hour24 =
            period === "AM" && hourPart === "12"
              ? "00"
              : period === "AM"
                ? hourPart.padStart(2, "0")
                : period === "PM" && hourPart === "12"
                  ? "12"
                  : String(parseInt(hourPart, 10) + 12).padStart(2, "0");
          return `${hour24}:${minute} ${period}`;
        },
      );

    labels.push({ hour, label: timeString });
  }

  return labels;
};

export const generateCalendarTimeSlots = (eventData, participatingCompanyData) => {
  const slots = [];
  let startHour = 8;
  let startMinute = 0;
  let endHour = 19;
  let endMinute = 0;

  if (eventData?.horaInicio && eventData?.horaFin) {
    const startTimeParts = eventData.horaInicio.split(":");
    startHour = parseInt(startTimeParts[0], 10);
    startMinute = parseInt(startTimeParts[1], 10);

    const endTimeParts = eventData.horaFin.split(":");
    endHour = parseInt(endTimeParts[0], 10);
    endMinute = parseInt(endTimeParts[1], 10);
  }

  const durationMinutes = getAppointmentDurationMinutes(eventData);
  const breakTimes = parseBreakTimes(participatingCompanyData);
  const { breakStartMinutes, breakEndMinutes } = getBreakMinutes(breakTimes);

  let currentHour = startHour;
  let currentMinute = startMinute;
  let cellIndex = 0;
  let lastHour = -1;
  let breakEndSlotInserted = false;

  while (
    currentHour < endHour ||
    (currentHour === endHour && currentMinute < endMinute)
  ) {
    const currentMinutes = currentHour * 60 + currentMinute;
    const slotEndMinutes = currentMinutes + durationMinutes;

    let isInBreak = false;
    if (breakStartMinutes !== null && breakEndMinutes !== null) {
      isInBreak =
        (currentMinutes >= breakStartMinutes &&
          currentMinutes < breakEndMinutes) ||
        (slotEndMinutes > breakStartMinutes &&
          slotEndMinutes <= breakEndMinutes) ||
        (currentMinutes < breakStartMinutes &&
          slotEndMinutes > breakEndMinutes);

      if (
        !isInBreak &&
        !breakEndSlotInserted &&
        currentMinutes > breakEndMinutes
      ) {
        const breakEndHour = Math.floor(breakEndMinutes / 60);
        const breakEndMin = breakEndMinutes % 60;
        const breakEndHasEnoughTime = isTimeSlotWithinEventEnd(
          breakEndHour,
          breakEndMin,
          durationMinutes,
          eventData,
        );

        slots.push({
          hour: breakEndHour,
          minute: breakEndMin,
          cellIndex,
          isFirstCellOfHour: breakEndHour !== lastHour,
          durationMinutes,
          isValid: breakEndHasEnoughTime,
        });
        lastHour = breakEndHour;
        cellIndex++;
        breakEndSlotInserted = true;

        currentHour = breakEndHour;
        currentMinute = breakEndMin;
        currentMinute += durationMinutes;
        if (currentMinute >= 60) {
          currentHour += Math.floor(currentMinute / 60);
          currentMinute = currentMinute % 60;
        }
        continue;
      }
    }

    const hasEnoughTime = isTimeSlotWithinEventEnd(
      currentHour,
      currentMinute,
      durationMinutes,
      eventData,
    );
    const isFirstCellOfHour = currentHour !== lastHour;

    slots.push({
      hour: currentHour,
      minute: currentMinute,
      cellIndex,
      isFirstCellOfHour,
      durationMinutes,
      isValid: hasEnoughTime && !isInBreak,
    });

    lastHour = currentHour;
    cellIndex++;

    currentMinute += durationMinutes;
    if (currentMinute >= 60) {
      currentHour += Math.floor(currentMinute / 60);
      currentMinute = currentMinute % 60;
    }
  }

  return slots;
};

export const isTimeSlotInPast = (date, timeSlot) => {
  const now = new Date();
  const slotDateTime = new Date(date);
  slotDateTime.setHours(timeSlot.hour, timeSlot.minute, 0, 0);
  return slotDateTime < now;
};

export const hasBusinessEventOnDate = (date, eventData) => {
  if (eventData?.fechaInicioEvento && eventData?.fechaFinEvento) {
    const eventStartDate = new Date(eventData.fechaInicioEvento + "T00:00:00");
    const eventEndDate = new Date(eventData.fechaFinEvento + "T00:00:00");
    return date >= eventStartDate && date <= eventEndDate;
  }
  return false;
};
